import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Apex.css';
import { useConfig } from '../ConfigContext';

function Apex() {
  const config = useConfig();
  const ap = config.apexPage ?? {};
  const av = ap.sections ?? {};
  const showPH = ap.showPlaceholders ?? true;
  const ctaChecklist = (ap.ctaChecklist ?? []).length > 0 ? ap.ctaChecklist : [
    "A small set of audio examples — all that's required",
    'Calibrates locally in minutes, no cloud pipeline',
    'Custom wake words with acoustic precision',
    'Keyword detection, wake words & transcription',
    'Edge hardware or cloud GPU deployment',
  ];
  const [activePipelineStep, setActivePipelineStep] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── DATA ─────────────────────────────────────────────────────

  const capabilities = (ap.capabilities ?? []).length > 0
    ? ap.capabilities.map((cap, i) => ({ id: i+1, title: cap.title, description: cap.desc, icon: cap.icon ?? null }))
    : [
        { id: 1, title: 'Adaptive Keyword Learning',        icon: '/icons/apex/adaptive-keyword.png',   description: 'Provide a small number of audio examples per term and Apex calibrates its detection engine for that vocabulary — delivering higher accuracy on the terms that matter most.' },
        { id: 2, title: 'Custom Wake Word Detection',       icon: '/icons/apex/custom-wake.png',        description: 'Calibrate Apex to your exact wake word with a small set of audio examples — delivering precision that generic voice systems cannot match.' },
        { id: 3, title: 'Keyword Detection',                icon: '/icons/apex/keyword.png',            description: "Apex delivers keyword detection with a higher accuracy ceiling for stable, known vocabularies — providing greater confidence across varied speakers and acoustic conditions." },
        { id: 4, title: 'Domain Vocabulary Transcription',  icon: '/icons/apex/domain-vocab.png',       description: 'Open-ended transcription calibrated for domain-specific terms. Provide audio examples and Apex renders them correctly where general-purpose ASR consistently fails.' },
        { id: 5, title: 'Edge & Cloud Ready',               icon: '/icons/apex/edge-cloud.png',         description: 'Deploy on embedded hardware, air-gapped systems, or GPU-backed cloud infrastructure. Same SDK, same API, same accuracy wherever your deployment requires.' },
        { id: 6, title: 'Separate SDK',                     icon: '/icons/apex/sdk.png',                description: 'Apex ships as a standalone SDK with its own install and class structure, sharing the same audio pipeline and VAD layer as Pryme.' },
      ];

  const useCases = (ap.useCases ?? []).length > 0
    ? ap.useCases.map((uc, i) => ({ id: i+1, category: uc.title, title: uc.title, description: uc.desc, features: uc.features ?? [] }))
    : [
        { id: 1, category: 'Defense & Government',  title: 'Callsign & Operational Term Detection', description: 'Learn specific callsigns, unit designators, and operational commands from a small set of recordings — in air-gapped environments, with no audio transmitted externally.', features: ['Small set of samples', 'Air-gapped deployment', 'On-chip inference'] },
        { id: 2, category: 'Healthcare',            title: 'Clinical Vocabulary Transcription',     description: 'Provide a few recordings of drug names, procedure codes, and specialist terms. Apex calibrates to their profile and transcribes them correctly — without sending audio to a cloud provider.', features: ['Domain vocabulary', 'No audio retained', 'Runtime model loading'] },
        { id: 3, category: 'Robotics & Automation', title: 'Voice-Commanded Systems',               description: "Calibrate Apex to the exact command vocabulary your system uses. Apex's multi-stage engine delivers high detection confidence on known commands.", features: ['High-confidence detection', 'Noise robust', 'Compact model size'] },
      ];

  const advantages = (ap.advantages ?? []).length > 0
    ? ap.advantages.map(a => ({ value: a.value, metric: a.metric, description: a.desc }))
    : [
        { value: 'Small set',  metric: 'Audio Examples Per Term',      description: 'All Apex needs to calibrate to a keyword, wake word, or domain term' },
        { value: 'Higher',     metric: 'Accuracy on Known Vocabulary',  description: 'Calibrated detection outperforms generic voice systems on stable, defined vocabularies' },
        { value: 'Minutes',    metric: 'Local Calibration Time',        description: 'Vocabulary calibration completes on your hardware — no cloud pipeline, no waiting' },
        { value: 'Edge→Cloud', metric: 'Deployment Flexibility',        description: 'Same SDK and API on embedded CPU or full-scale GPU infrastructure' },
      ];

  const PIPELINE_DEFAULTS = [
    { id: 'samples', number: '01', iconSrc: '/icons/apex/audio-sample.png',    label: 'Audio Sample Collection',
      detail: 'Each audio sample should be a clean recording of the target term spoken naturally. Apex accepts standard audio formats and handles resampling, normalization, and VAD segmentation. The collection step can be completed in minutes for a typical vocabulary.' },
    { id: 'train',   number: '02', iconSrc: '/icons/apex/vocab-calibration.png', label: 'Vocabulary Calibration',
      detail: 'Calibration runs entirely on your hardware with no data leaving your environment. The output is lightweight and optimized for inference on your target platform. Calibration time for a typical vocabulary is measured in seconds to minutes, not hours.' },
    { id: 'vad',     number: '03', iconSrc: '/icons/apex/vad.png',         label: 'Voice Activity Detection',
      detail: "Apex shares Pryme's VAD architecture — a dedicated neural model that distinguishes speech from non-speech in real time with configurable silence gating. Only genuine speech segments reach the detection and transcription layers." },
    { id: 'infer',   number: '04', iconSrc: '/icons/apex/multi-stage.png',   label: 'Multi-Stage Inference',
      detail: "Inference runs entirely on-device using the calibrated configuration produced during the vocabulary step. Apex's proprietary detection engine achieves higher confidence scores and lower false-positive rates on known vocabularies." },
    { id: 'result',  number: '05', iconSrc: '/icons/apex/result.png',      label: 'Structured Result',
      detail: 'In keyword and wake word detection mode, the result includes whether a detection event fired, which term matched, and the confidence score. Word-level timestamps are included on every transcription result.' },
  ];
  const pipelineSteps = PIPELINE_DEFAULTS.map((def, i) => ({
    ...def,
    label:  ap.pipeline?.[i]?.label  || def.label,
    detail: ap.pipeline?.[i]?.detail || ap.pipeline?.[i]?.summary || def.detail,
  }));

  const [activeCompCol, setActiveCompCol] = useState(0);
  const comparison = [
    { feature: 'Setup Input',           apex: 'A small set of audio examples', pryme: 'Text label — no audio required', traditional: 'Hundreds of labeled recordings' },
    { feature: 'Calibration Time',      apex: 'Minutes, runs locally',         pryme: 'None — deploy same day',       traditional: 'Hours to days, cloud pipeline' },
    { feature: 'Detection Method',     apex: 'Multi-stage calibrated engine',  pryme: 'Multi-stage detection engine',  traditional: 'Full model fine-tuning' },
    { feature: 'Custom Wake Words',    apex: 'Yes — calibrated precision',     pryme: 'Yes — no samples required',     traditional: 'Yes — requires full dataset' },
    { feature: 'Domain Transcription', apex: 'Yes — acoustically learned',    pryme: 'Approximate (semantic match)', traditional: 'Requires fine-tuned ASR model' },
    { feature: 'New Term Setup',       apex: 'Record 5–10 samples, train',    pryme: 'Instant — define in text',     traditional: 'Full retrain cycle' },
    { feature: 'Deployment',           apex: 'Edge CPU or cloud GPU',         pryme: 'Edge, on-chip, or cloud',      traditional: 'Typically cloud only' },
    { feature: 'Audio Data Retention', apex: 'None — fully local option',     pryme: 'None — fully local option',    traditional: 'Varies' },
    { feature: 'SDK',                  apex: 'Separate Apex SDK',             pryme: 'Tytonvoice SDK',               traditional: 'Custom integration' },
    { feature: 'VAD',                  apex: 'Neural, shared with Pryme',     pryme: 'Neural, configurable gating',  traditional: 'Energy-based or none' },
  ];

  return (
    <div className="apex-page">

      {/* ── HERO ── */}
      <section className="apex-hero">
        <div className="apex-hero-left">
          <div className="hero-badge">Apex</div>
          <h1 className="hero-title">Keyword Detection.<br />Calibrated to<br />Your Vocabulary.</h1>
          <p className="hero-subtitle">
            {ap.heroSubtitle ?? 'Apex is the precision step up from Pryme. Provide a small set of audio examples and Apex calibrates its multi-stage detection engine to your exact keywords, wake words, and domain vocabulary — delivering accuracy that generic voice systems cannot match.'}
          </p>
          <div className="hero-cta-group">
            <Link to="/download" className="cta-button primary">
              Request Demo
            </Link>
          </div>
          <div className="hero-stat-row">
            <div className="hero-stat">
              <span className="hero-stat-value">5–10</span>
              <span className="hero-stat-label">Audio samples per term</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="hero-stat-value">Minutes</span>
              <span className="hero-stat-label">Training time, runs locally</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <span className="hero-stat-value">Higher</span>
              <span className="hero-stat-label">Accuracy vs generic systems</span>
            </div>
          </div>
        </div>

        <div className="apex-hero-right">
          {ap.heroImage
            ? <img src={ap.heroImage} alt="Apex Hero Visual" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px', opacity: 0.25 }} />
            : showPH && <div className="visual-placeholder-box apex-hero-visual-box">
                <span className="visual-placeholder-label">Hero Visual</span>
                <span className="visual-placeholder-sub">Calibration flow — audio examples → calibration → detection</span>
              </div>
          }
        </div>
      </section>

      {/* ── CORE INSIGHT ── */}
      {(av.insight ?? true) && (
      <section className="apex-core-insight">
        <div className="insight-text">
          <h2>When You Need More Precision</h2>
          <p>
            {ap.insightPara1 ?? "Pryme detects any keyword the moment you define it in text — no audio samples required. That's the right tool when speed of deployment matters most and vocabulary changes frequently. But for stable, known vocabularies — brand wake words, operational callsigns, clinical terminology — there's a higher accuracy ceiling available."}
          </p>
          <p>
            <strong>Apex reaches that ceiling.</strong> {ap.insightPara2 ?? "Provide a small set of audio recordings per term and Apex calibrates its detection engine to your exact vocabulary — in your speakers' voices, in your acoustic environment. The result is higher detection confidence, lower false-positive rates, and accurate transcription of domain terms that general-purpose ASR engines consistently mis-render."}
          </p>
        </div>

        {ap.comparisonImage
          ? <img src={ap.comparisonImage} alt="Apex Comparison" style={{ width:'100%', borderRadius:'8px', marginTop:'2rem' }} />
          : showPH && <div className="visual-placeholder-box wide">
              <span className="visual-placeholder-label">Comparison Graphic</span>
              <span className="visual-placeholder-sub">Pryme vs Apex accuracy on known vocabulary</span>
            </div>
        }
      </section>
      )}

      {/* ── PIPELINE + PRYME VS APEX — shared gradient band ── */}
      <div className="apex-pipeline-band">

        {/* ── PIPELINE ── */}
        <section className="apex-pipeline-section" id="how-it-works">
          <div className="section-header">
            <h2>The Apex Pipeline</h2>
            <p className="section-subtitle">
              Five stages from audio sample collection to a high-confidence detection event.
              Select any stage to explore it in depth.
            </p>
          </div>

          <div className="apex-pipeline-container">
            {pipelineSteps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div
                  className={`apex-pipeline-step ${activePipelineStep === step.id ? 'active' : ''}`}
                  onClick={() => setActivePipelineStep(activePipelineStep === step.id ? null : step.id)}
                >
                  <div className="apex-pipeline-step-header">
                    <span className="apex-pipeline-step-number">{step.number}</span>
                    <h3 className="apex-pipeline-step-label">{step.label}</h3>
                  </div>
                  <div className="apex-pipeline-step-icon">
                    {step.iconSrc && <img src={step.iconSrc} alt={step.label} />}
                  </div>
                  {activePipelineStep === step.id && (
                    <div className="apex-pipeline-step-detail">
                      <p>{step.detail}</p>
                    </div>
                  )}
                </div>
                {idx < pipelineSteps.length - 1 && (
                  <div className="apex-pipeline-connector">→</div>
                )}
              </React.Fragment>
            ))}
          </div>

          {ap.pipelineImage
            ? <img src={ap.pipelineImage} alt="Apex Pipeline" style={{ width:'100%', borderRadius:'8px', marginTop:'3rem' }} />
            : showPH && <div className="visual-placeholder-box wide" style={{ marginTop:'3rem' }}>
                <span className="visual-placeholder-label">Pipeline Diagram</span>
                <span className="visual-placeholder-sub">Sample Collection → Calibration → VAD → Multi-Stage Inference → Result</span>
              </div>
          }
        </section>

        {/* ── PRYME VS APEX ── */}
        <section className="apex-modes-section">
          <div className="section-header">
            <h2>Pryme or Apex?</h2>
            <p className="section-subtitle">
              Both products share the same zero-infrastructure philosophy.
              The difference is the accuracy trade-off you're making.
            </p>
          </div>
          <div className="apex-modes-grid">

            <div className="apex-mode-card">
              <div className="apex-mode-card-header">
                <span className="apex-mode-label">No Samples</span>
                <h3 className="apex-mode-class">Pryme</h3>
              </div>
              {showPH && <div className="visual-placeholder-box apex-mode-visual">
                <span className="visual-placeholder-label">Visual</span>
                <span className="visual-placeholder-sub">Text label → deploy same day</span>
              </div>}
              <p className="apex-mode-description">
                Define any keyword in plain text and start detecting immediately —
                no samples, no training step, no redeployment. The right choice when
                vocabulary changes frequently or time-to-deploy is the priority.
              </p>
              <ul className="apex-mode-features">
                <li>Zero audio samples required</li>
                <li>Instant — define and deploy same day</li>
                <li>Live label changes at runtime</li>
                <li>Multi-stage detection engine</li>
              </ul>
            </div>

            <div className="apex-mode-card apex-mode-card-alt">
              <div className="apex-mode-card-header">
                <span className="apex-mode-label apex-mode-label-alt">Calibrated</span>
                <h3 className="apex-mode-class">Apex</h3>
              </div>
              {showPH && <div className="visual-placeholder-box apex-mode-visual">
                <span className="visual-placeholder-label">Visual</span>
                <span className="visual-placeholder-sub">Audio examples → calibration → high-confidence detection</span>
              </div>}
              <p className="apex-mode-description">
                Provide 5–10 audio recordings per term. Apex trains a compact acoustic
                engine in minutes and delivers higher accuracy on known, stable
                vocabularies. The right choice when detection confidence and
                calibrated precision are the priority.
              </p>
              <ul className="apex-mode-features">
                <li>5–10 audio samples per term</li>
                <li>Trains locally in minutes</li>
                <li>Higher accuracy on known vocabulary</li>
                <li>Custom wake words, domain transcription</li>
              </ul>
            </div>

          </div>
        </section>

      </div>{/* end apex-pipeline-band */}

      {/* ── CAPABILITIES ── */}
      {(av.capabilities ?? true) && (
      <section className="apex-capabilities-section" id="capabilities">
        <div className="section-header apex-section-header-dark">
          <h2>Core Capabilities</h2>
          <p className="section-subtitle">
            What calibrated multi-stage detection unlocks for your deployment
          </p>
        </div>
        <div className="apex-capabilities-grid">
          {capabilities.map((cap) => (
            <div key={cap.id} className="apex-capability-card">
              <div className="apex-capability-icon-wrapper">
                  {cap.icon && <img src={cap.icon} alt={cap.title} className="apex-capability-icon-img" />}
                </div>
              <h3>{cap.title}</h3>
              <p>{cap.description}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── ADVANTAGES ── */}
      {(av.advantages ?? true) && (
      <section className="apex-advantages-section">
        <div className="section-header">
          <h2>What Makes Apex Different</h2>
          <p className="section-subtitle">
            The metrics that define a calibrated detection system
          </p>
        </div>
        <div className="apex-advantages-grid">
          {advantages.map((adv, idx) => (
            <div key={idx} className="apex-advantage-card">
              <div className="apex-advantage-value">{adv.value}</div>
              <div className="apex-advantage-metric">{adv.metric}</div>
              <div className="apex-advantage-description">{adv.description}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── USE CASES ── */}
      {(av.useCases ?? true) && (
      <section className="apex-use-cases-section">
        <div className="section-header">
          <h2>Real-World Applications</h2>
          <p className="section-subtitle">
            Any domain where detection accuracy on known vocabulary is non-negotiable
          </p>
        </div>
        <div className="apex-use-cases-grid">
          {useCases.map((uc) => (
            <div
              key={uc.id}
              className={`apex-use-case-card ${expandedCard === uc.id ? 'expanded' : ''}`}
              onClick={() => setExpandedCard(expandedCard === uc.id ? null : uc.id)}
            >
              <div className="use-case-header">
                <div className="use-case-category">{uc.category}</div>
                <h3>{uc.title}</h3>
              </div>
              <p className="use-case-description">{uc.description}</p>
              <div className="use-case-features">
                {uc.features.map((f, i) => (
                  <span key={i} className="feature-tag">{f}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── COMPARISON TABLE ── */}
      {(av.comparison ?? true) && (
      <section className="apex-comparison-section">
        <div className="section-header">
          <h2>Pryme · Apex · Traditional ASR</h2>
          <p className="section-subtitle">
            How Pryme, Apex, and traditional voice systems compare
          </p>
        </div>
        {/* Desktop */}
        <div className="apex-comparison-table comparison-desktop-only">
          <div className="apex-comparison-row header">
            <div className="apex-comparison-cell feature">Feature</div>
            <div className="apex-comparison-cell">Apex</div>
            <div className="apex-comparison-cell">Pryme</div>
            <div className="apex-comparison-cell">Traditional ASR</div>
          </div>
          {comparison.map((row, idx) => (
            <div key={idx} className="apex-comparison-row">
              <div className="apex-comparison-cell feature">{row.feature}</div>
              <div className="apex-comparison-cell highlight">{row.apex}</div>
              <div className="apex-comparison-cell">{row.pryme}</div>
              <div className="apex-comparison-cell">{row.traditional}</div>
            </div>
          ))}
        </div>
        {/* Mobile */}
        <div className="comp-mobile">
          <div className="comp-mobile-tabs">
            {['Pryme', 'Traditional ASR'].map((label, i) => (
              <button key={i} className={`comp-mobile-tab${activeCompCol === i ? ' active' : ''}`} onClick={() => setActiveCompCol(i)}>
                {label}
              </button>
            ))}
          </div>
          <div className="comp-mobile-header-row">
            <div className="comp-mobile-th comp-mobile-feature-th">Feature</div>
            <div className="comp-mobile-th comp-mobile-hero-th">Apex</div>
            <div className="comp-mobile-th">{activeCompCol === 0 ? 'Pryme' : 'Traditional ASR'}</div>
          </div>
          {comparison.map((row, idx) => (
            <div key={idx} className="comp-mobile-row">
              <div className="comp-mobile-feature">{row.feature}</div>
              <div className="comp-mobile-val comp-mobile-val-hero">{row.apex}</div>
              <div className="comp-mobile-val">{activeCompCol === 0 ? row.pryme : row.traditional}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* ── PRIVACY CALLOUT ── */}
      <section className="apex-privacy-section">
        <div className="privacy-box">
            <img src="/icons/general/privacy.png" alt="Privacy" className="privacy-icon-img" />
            <div className="privacy-content">
            <h3>Audio Stays Where You Put It</h3>
            <p>
              Vocabulary calibration runs entirely locally — your audio examples never
              leave your environment. The resulting configuration is compact and self-contained.
              In edge and air-gapped deployments, the complete Apex pipeline —
              calibration, VAD, and inference — runs on-device with no external
              data dependency. For cloud deployments, you control the infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="apex-cta-section">
        <div className="apex-cta-split">
          <div className="apex-cta-left">
            <p className="apex-cta-eyebrow">Get started with</p>
            <h2 className="apex-cta-heading">
              <span className="apex-cta-brand">APEX</span>
              <span className="apex-cta-descriptor">Calibrated Detection</span>
            </h2>
            <p className="apex-cta-body">
              {ap.ctaBody ?? 'Provide a small set of audio examples per term and deploy calibrated keyword detection, custom wake words, and domain transcription — on embedded hardware or at cloud scale — in minutes.'}
            </p>
            <Link to="/download" className="cta-button primary large apex-cta-demo-btn">
              Request Demo
            </Link>
          </div>
          <div className="apex-cta-right">
            <ul className="apex-cta-checklist">
              {ctaChecklist.map((item, i) => (
                <li key={i} className="apex-cta-check-item">
                  <span className="apex-cta-check-icon">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Apex;